// =============================================
// SeedsTab — ONLY seed management (generate, edit, delete)
// =============================================
// Draft flow (2026-04-19):
//   Wygenerowane seedy sa ZAWSZE zapisywane do DB od razu ze `status='draft'`.
//   Dzieki temu user moze zamknac karte po generacji i wrocic pozniej — draft
//   seedy czekaja na akceptacje lub odrzucenie. Accept → UPDATE `status='active'`,
//   Reject → DELETE. Brak utraty danych przy zamknieciu karty w trakcie generacji.

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sprout, Trash2, ArrowRight, CheckCircle2, XCircle, AlertCircle, Loader2, Cpu } from "lucide-react";
import { GenerateButton } from "./GenerateButton";
import { useThematicSeeds } from "@/hooks/useThematicSeeds";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useMapGenerator } from "@/hooks/useMapGenerator";
import { useWorkerJob } from "@/hooks/useWorkerJob";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { ThematicMap } from "@/types/thematicMaps";

interface SeedsTabProps {
  mapId: string;
  map: ThematicMap;
  onNext?: () => void;
}

export function SeedsTab({ mapId, map, onNext }: SeedsTabProps) {
  const { seeds, createSeedsBulk, deleteSeed, refreshSeeds } = useThematicSeeds(mapId);
  const { nodes } = useThematicNodes(mapId);
  const { job: workerJob } = useWorkerJob(mapId, "map_seeds");
  const { generating, runSeedGeneration } = useMapGenerator(mapId);
  const { toast } = useToast();
  const [processingDraft, setProcessingDraft] = useState(false);

  // Podziel seedy na draft (propozycje) vs pozostale (zaakceptowane/w produkcji)
  const draftSeeds = seeds.filter((s) => s.status === "draft");
  const activeSeeds = seeds.filter((s) => s.status !== "draft");

  const handleGenerateSeeds = async () => {
    const result = await runSeedGeneration(map);
    if (result) {
      // Zapisz od razu do DB jako draft — survive reload
      await createSeedsBulk(
        result.map((s) => ({ ...s, status: "draft" as const })),
      );
    }
  };

  const handleAcceptAllDrafts = async () => {
    if (draftSeeds.length === 0) return;
    setProcessingDraft(true);
    try {
      const draftIds = draftSeeds.map((s) => s.id);
      const { error } = await (supabase as any)
        .from("thematic_seeds")
        .update({ status: "active" })
        .in("id", draftIds);
      if (error) throw error;
      await refreshSeeds();
      toast({ title: "Zaakceptowane", description: `${draftIds.length} seeds przeniesiono do produkcji` });
    } catch (err) {
      toast({
        title: "Blad",
        description: err instanceof Error ? err.message : "Nie udalo sie zaakceptowac",
        variant: "destructive",
      });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleRejectAllDrafts = async () => {
    if (draftSeeds.length === 0) return;
    if (!confirm(`Odrzucic ${draftSeeds.length} propozycji seedow? (DELETE z bazy)`)) return;
    setProcessingDraft(true);
    try {
      const draftIds = draftSeeds.map((s) => s.id);
      const { error } = await (supabase as any)
        .from("thematic_seeds")
        .delete()
        .in("id", draftIds);
      if (error) throw error;
      await refreshSeeds();
      toast({ title: "Odrzucone", description: `${draftIds.length} propozycji usunieto` });
    } catch (err) {
      toast({
        title: "Blad",
        description: err instanceof Error ? err.message : "Nie udalo sie odrzucic",
        variant: "destructive",
      });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleAcceptOne = async (seedId: string) => {
    setProcessingDraft(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_seeds")
        .update({ status: "active" })
        .eq("id", seedId);
      if (error) throw error;
      await refreshSeeds();
    } catch (err) {
      toast({
        title: "Blad",
        description: err instanceof Error ? err.message : "Nie udalo sie zaakceptowac",
        variant: "destructive",
      });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleRejectOne = async (seedId: string) => {
    setProcessingDraft(true);
    try {
      await deleteSeed(seedId);
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleDeleteSeed = async (e: React.MouseEvent, seedId: string, seedName: string) => {
    e.stopPropagation();
    if (confirm(`Usunąć seed "${seedName}" i wszystkie jego nodes?`)) {
      await deleteSeed(seedId);
    }
  };

  const getNodeCount = (seedId: string) => nodes.filter((n) => n.seed_id === seedId).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold mb-1">Seeds (klastry tematyczne)</h2>
        <p className="text-sm text-muted-foreground">
          Seeds to grupy tematyczne wyodrębnione z Macro Statement. Każdy seed to osobna gałąź tematyczna serwisu.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">
            {activeSeeds.length} seeds
          </Badge>
          {draftSeeds.length > 0 && (
            <Badge variant="outline" className="text-sm px-3 py-1 border-amber-500/50 text-amber-500 bg-amber-500/10">
              {draftSeeds.length} propozycji (draft)
            </Badge>
          )}
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {nodes.length} nodes łącznie
          </Badge>
        </div>
        <GenerateButton
          onClick={handleGenerateSeeds}
          generating={generating}
          label="Generuj Seeds z AI"
          loadingLabel="Generowanie seeds..."
        />
      </div>

      {/* WORKER JOB BANNER — aktywny worker generuje seedy (Realtime status) */}
      {workerJob && (workerJob.status === "queued" || workerJob.status === "in_progress") && (
        <div className="rounded-lg border-2 border-blue-500/50 bg-blue-500/10 p-3 flex items-center gap-3">
          {workerJob.status === "in_progress" ? (
            <Loader2 className="h-5 w-5 text-blue-400 animate-spin shrink-0" />
          ) : (
            <Cpu className="h-5 w-5 text-blue-400 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-blue-300">
              {workerJob.status === "queued" ? "Worker: czeka w kolejce..." : "Worker: generuje seedy..."}
            </div>
            {workerJob.progressLabel && (
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {workerJob.progressLabel}
              </div>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground/70 font-mono uppercase tracking-wider shrink-0">
            Job #{workerJob.queueRowId.slice(0, 8)}
          </span>
        </div>
      )}
      {workerJob && workerJob.status === "failed" && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-400">Worker: generowanie seedow padlo</div>
            {workerJob.error && (
              <div className="text-xs text-red-300/80 mt-0.5 font-mono">{workerJob.error}</div>
            )}
          </div>
        </div>
      )}

      {/* DRAFT SEEDS PANEL — propozycje czekajace na akceptacje (survive reload!) */}
      {draftSeeds.length > 0 && (
        <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <div>
                <h3 className="font-semibold text-amber-700 dark:text-amber-300">
                  Propozycje seedow ({draftSeeds.length}) — czekaja na akceptacje
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Zapisane w bazie jako draft. Mozesz zamknac karte i wrocic pozniej.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAllDrafts}
                disabled={processingDraft}
                className="border-red-500/40 text-red-500 hover:bg-red-500/10"
              >
                <XCircle className="h-4 w-4 mr-1.5" />
                Odrzuc wszystkie
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAllDrafts}
                disabled={processingDraft}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Zaakceptuj wszystkie
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            {draftSeeds.map((seed, i) => (
              <div
                key={seed.id}
                className="flex items-center justify-between bg-background/60 rounded-md px-3 py-2 border border-amber-500/20"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <span className="font-semibold truncate">{seed.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">/{seed.slug}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">P{seed.priority}</Badge>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-emerald-500 hover:bg-emerald-500/10"
                    onClick={() => handleAcceptOne(seed.id)}
                    disabled={processingDraft}
                    title="Zaakceptuj ten seed"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:bg-red-500/10"
                    onClick={() => handleRejectOne(seed.id)}
                    disabled={processingDraft}
                    title="Odrzuc (usun z bazy)"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seeds List — tylko zaakceptowane (non-draft) */}
      {activeSeeds.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center">
          <Sprout className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-lg font-medium text-muted-foreground">Brak seeds</p>
          <p className="text-sm text-muted-foreground mt-1">
            Kliknij "Generuj Seeds z AI" — system wygeneruje 5-8 klastrów tematycznych z Twojego Macro Statement.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {activeSeeds.map((seed, i) => {
            const nodeCount = getNodeCount(seed.id);
            return (
              <div
                key={seed.id}
                className="border rounded-lg p-4 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                      {i + 1}
                    </div>
                    <div>
                      <span className="font-semibold">{seed.name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">/{seed.slug}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {seed.status && seed.status !== "active" && (
                      <Badge
                        variant="outline"
                        className={
                          seed.status === "pending_production"
                            ? "border-amber-500/50 text-amber-600 dark:text-amber-400"
                            : seed.status === "deprecated" || seed.status === "archived"
                            ? "border-red-500/50 text-red-600 dark:text-red-400"
                            : "border-sky-500/50 text-sky-600 dark:text-sky-400"
                        }
                        title={`seed.status = ${seed.status}`}
                      >
                        {seed.status === "pending_production"
                          ? "W produkcji"
                          : seed.status === "deprecated"
                          ? "Przestarzały"
                          : seed.status === "archived"
                          ? "Zarchiwizowany"
                          : "Szkic"}
                      </Badge>
                    )}
                    <Badge variant="outline">P{seed.priority}</Badge>
                    <Badge variant="secondary">{nodeCount} nodes</Badge>
                    {seed.intent_family?.length > 0 && (
                      <div className="flex gap-1">
                        {seed.intent_family.slice(0, 3).map((intent, j) => (
                          <Badge key={j} variant="outline" className="text-xs">
                            {intent}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={(e) => handleDeleteSeed(e, seed.id, seed.name)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Next step — tylko gdy sa zaakceptowane seedy i BRAK draftów */}
      {activeSeeds.length > 0 && draftSeeds.length === 0 && onNext && (
        <div className="flex justify-end pt-4">
          <Button onClick={onNext} size="lg">
            Następny krok: Nodes
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
